import Component from '@cx-modules/trends-card';
import TrendsCardCss from '@cx-modules/trends-card/style.css?inline';
import {
  fetchCampaignData,
  widgetBootstrap
} from '../../services/cx-module-helper';
import css from '../../utils/css';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const { VITE_SPORTS_DATA_API_HOST: dataApiEndpoint } = import.meta.env;

type Bookmaker = { bookmakerName?: string };

type ContractParams = {
  affIds?: unknown;
  apiKey?: unknown;
  analytics?: { getDataLayer: () => unknown };
  bookmakers?: Record<string, Bookmaker>;
  campaignName?: unknown;
  categoryId?: unknown;
  categoryGroupId?: unknown;
  countryCode?: string;
  enableClickouts?: unknown;
  eventId?: unknown;
  forceDesktopView?: unknown;
  forceMobileView?: unknown;
  hideBets?: unknown;
  limit?: string;
  marketId?: unknown;
  oddsFormat?: unknown;
  repub?: unknown;
  showTrendsRationale?: unknown;
  subdivisionCode?: string;
  subeventId?: unknown;
  widgetStyle?: string;
  hash?: unknown;
};

type ContractProps = {
  affIds?: unknown;
  apiKey?: unknown;
  dataApiEndpoint?: string;
  disableAnalytics: boolean;
  forceDesktopView?: unknown;
  forceMobileView?: unknown;
  analyticsImpressionEventData: unknown;
  analyticsImpressionEventName: string;
  bookies?: Record<string, Bookmaker>;
  countryCode?: string;
  enableClickouts?: unknown;
  hideBets?: unknown;
  oddsFormat?: unknown;
  repub?: unknown;
  showTrendsRationale?: unknown;
  theme?: string;
  subeventId?: unknown;
  eventId?: unknown;
  categoryId?: unknown;
  categoryGroupId?: unknown;
  marketId?: unknown;
  limit?: number;
  analyticsDataLayer?: unknown;
  subdivisionCode?: string;
};

export const contract = ({
  affIds,
  apiKey,
  analytics,
  bookmakers,
  campaignName,
  categoryId,
  categoryGroupId,
  countryCode,
  enableClickouts,
  eventId,
  forceDesktopView,
  forceMobileView,
  hideBets,
  limit,
  marketId,
  oddsFormat,
  repub,
  showTrendsRationale,
  subdivisionCode,
  subeventId,
  widgetStyle: theme,
  hash: widgetSlot
}: ContractParams) => {
  const props: ContractProps = {
    affIds,
    apiKey,
    dataApiEndpoint,
    disableAnalytics: !analytics,
    forceDesktopView,
    forceMobileView,
    analyticsImpressionEventData: {
      bookieCode: Object.keys(bookmakers || {}),
      bookieName: Object.values(bookmakers || {}).map(b => b.bookmakerName),
      campaignName,
      widgetType: 'top-trends',
      widgetSlot,
      variation: 'trends-cards'
    },
    analyticsImpressionEventName: 'widget_impression',
    bookies: bookmakers,
    countryCode: countryCode ?? 'US',
    enableClickouts,
    hideBets,
    oddsFormat,
    repub,
    showTrendsRationale,
    theme,
    subeventId,
    eventId,
    categoryId,
    categoryGroupId,
    marketId,
    limit: limit ? parseInt(limit, 10) : undefined
  };

  const countriesRequiringSubdivisionCode = ['US', 'CA'];
  const normalisedCountryCode = props.countryCode?.toUpperCase();

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  if (
    normalisedCountryCode &&
    countriesRequiringSubdivisionCode.includes(normalisedCountryCode)
  ) {
    props.subdivisionCode = subdivisionCode ?? 'NJ';
  }

  return props;
};

type CampaignAttr = HTMLElement & {
  slotId?: string;
  overrideUrl?: string;
  subeventId?: string | null;
  marketId?: string | null;
  sport?: string | null;
  competition?: string | null;
  participant?: string | null;
};

const fetchCampaign = (attr: CampaignAttr) => {
  return fetchCampaignData(
    attr.slotId ?? '',
    attr.overrideUrl ?? '',
    {
      ocSubeventId: attr.subeventId,
      ocMarketId: attr.marketId,
      sport: attr.sport,
      competition: attr.competition,
      participant: attr.participant
    },
    attr
  );
};

type CampaignData = {
  bookmakers?: Record<string, Bookmaker>;
  categoryId?: unknown;
  eventId?: unknown;
  limit?: unknown;
  marketId?: unknown;
  subeventId?: unknown;
};

// Cache key matching getTrendsCardCacheKey from cx-modules/trends-card
const queryKey = (campaignData: CampaignData) => [
  'trends-card',
  {
    bookmakerCodes: campaignData.bookmakers
      ? Object.keys(campaignData.bookmakers)
      : undefined,
    categoryId: campaignData.categoryId,
    eventId: campaignData.eventId,
    limit: campaignData.limit,
    marketId: campaignData.marketId,
    subeventId: campaignData.subeventId
  }
];

// Shadow DOM resets — cxy-components CSS targets `html` and `*` for base resets,
// which don't apply inside shadow DOM, breaking button styles and image sizing.
const customCss = `
  :host {
    box-sizing: border-box;
    text-rendering: optimizelegibility;
    -webkit-font-smoothing: antialiased;
    -moz-font-smoothing: antialiased;
  }

  :host,
  :host *,
  :host *::before,
  :host *::after {
    box-sizing: border-box;
  }

  button {
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
  }

  button img[alt] {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  button[class*='oddsButton'] {
    margin: 0;
    width: 100%;
  }

  button[class*='oddsButton'] [class*='oddsContainer'] [class*='line'],
  button[class*='oddsButton'] [class*='oddsContainer'] [class*='odds'] {
    display: flex;
    align-items: center;
  }
`;

widgetBootstrap({
  attributes,
  contract,
  Component,
  customCss,
  fetchCampaign,
  queryKey,
  style: TrendsCardCss,
  themeContract: css`
    #root > div {
      --trends-card-footer-bg: var(--widget-accent-background-color);
      --trends-card-header-bg: var(--widget-accent-background-color);
      --trends-card-main-bg: var(--widget-background-color);
      --trends-card-border-radius: var(--widget-border-radius, 12px);
      --trends-card-odds-radius: var(--widget-cta-border-radius);
      --trends-card-accent: var(--cxy-color-success);
      --trends-card-track-bg: #aaa3;

      --cxy-color-content-default: var(--widget-text-color);
      --cxy-color-success: var(--widget-positive-color, #23aa23);
      --cxy-color-container-danger: var(--widget-negative-color, #cc0000);

      [class*='_cardHeader_'] *,
      [class*='_trendContent_'] [class*='_narrative_'],
      [class*='_trendContent_'] [class*='_indicatorWrapper_'] > *,
      [class*='_trendContent_'] [class*='_percentage_'],
      [class*='_statBox_'][class*='_active_'] > * {
        color: var(--widget-accent-text-color);
      }

      [class*='_oddsButton_'] {
        --trends-card-odds-bg: var(--widget-cta-background-color);
        color: var(
          --widget-cta-color
        ) !important; /* overriding another !important from the module */

        [class*='_avatar_'] {
          border-radius: var(--widget-cta-inner-border-radius, 36px);
        }

        &:hover {
          opacity: 1;
          background-color: var(--widget-cta-hover-color) !important;
        }
      }

      /* tab's border radius follows inner border radius */
      [class*='_statsSection_'] [class*='_statBox_'] {
        border-radius: var(--widget-inner-border-radius, 8px)
          var(--widget-inner-border-radius, 8px) 0 0;
      }

      /* flame bg */
      [class*='_betType_'] {
        --cxy-color-container-subtle: var(--trends-card-main-bg);
      }

      /* remove peeking in corners, but unused background color */
      [class*='_trendsCardList_'] [class*='_card_'] {
        background: none;
      }
    }
  `,
  name: widgetName,
  version: '1.0.1' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
